import { useState, type PointerEvent } from 'react';
import { tokens } from '@/design';
import {
  editLayoutGraphicVertex,
  insertLayoutGraphicVertex,
  normalizedPaths,
  removeLayoutGraphicVertex,
} from './layoutGraphicGeometry';
import type {
  LayoutGraphicElement,
  LayoutGraphicGeometry,
  LayoutGraphicPoint,
} from './layoutTypes';

interface Vertex {
  readonly pathIndex: number;
  readonly vertexIndex: number;
}

interface Props {
  readonly graphic: LayoutGraphicElement;
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly onGeometryChange: (geometry: LayoutGraphicGeometry) => void;
  readonly onFinish: () => void;
  readonly onCancel: () => void;
}

export function LayoutGraphicVertexOverlay({
  graphic,
  pageWidthMm,
  pageHeightMm,
  onGeometryChange,
  onFinish,
  onCancel,
}: Props) {
  const [selected, setSelected] = useState<Vertex | null>(null);
  const [mode, setMode] = useState<'select' | 'add' | 'continue'>('select');
  const [dragging, setDragging] = useState<Vertex | null>(null);
  const paths = normalizedPaths(graphic.geometry);

  const normalizedPoint = (
    event: Pick<PointerEvent<SVGSVGElement>, 'clientX' | 'clientY' | 'currentTarget'>,
  ): LayoutGraphicPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const pageX = ((event.clientX - bounds.left) / bounds.width) * pageWidthMm;
    const pageY = ((event.clientY - bounds.top) / bounds.height) * pageHeightMm;
    return {
      x: (pageX - graphic.rect.x) / graphic.rect.width,
      y: (pageY - graphic.rect.y) / graphic.rect.height,
    };
  };

  return (
    <div
      data-testid="layout-graphic-vertex-overlay"
      style={{ position: 'absolute', inset: 0, zIndex: 120, pointerEvents: 'none' }}
    >
      <div
        role="toolbar"
        aria-label="Edit vertices"
        style={{
          position: 'absolute',
          top: 6,
          left: 6,
          display: 'flex',
          gap: 2,
          padding: 3,
          background: tokens.color.surface[0],
          border: tokens.border.thin,
          pointerEvents: 'auto',
          zIndex: 2,
        }}
      >
        <button type="button" aria-pressed={mode === 'add'} onClick={() => setMode(mode === 'add' ? 'select' : 'add')}>Add vertex</button>
        <button
          type="button"
          disabled={!selected}
          onClick={() => {
            if (!selected) return;
            onGeometryChange(removeLayoutGraphicVertex(graphic.geometry, selected.pathIndex, selected.vertexIndex));
            setSelected(null);
          }}
        >Delete vertex</button>
        <button type="button" aria-pressed={mode === 'continue'} onClick={() => setMode(mode === 'continue' ? 'select' : 'continue')}>Continue</button>
        <button type="button" onClick={onFinish}>Finish</button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
      <svg
        aria-label="Graphic vertices"
        viewBox={`0 0 ${pageWidthMm} ${pageHeightMm}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'auto' }}
        onPointerMove={(event) => {
          if (!dragging) return;
          onGeometryChange(editLayoutGraphicVertex(
            graphic.geometry,
            dragging.pathIndex,
            dragging.vertexIndex,
            normalizedPoint(event),
          ));
        }}
        onPointerUp={() => setDragging(null)}
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget || mode === 'select') return;
          const pathIndex = selected?.pathIndex ?? 0;
          const path = paths[pathIndex];
          if (!path) return;
          const after = mode === 'continue'
            ? path.points.length - 1
            : selected?.vertexIndex ?? path.points.length - 1;
          const geometry = insertLayoutGraphicVertex(
            graphic.geometry,
            pathIndex,
            after,
            normalizedPoint(event),
          );
          onGeometryChange(geometry);
          setSelected({ pathIndex, vertexIndex: after + 1 });
        }}
      >
        {paths.flatMap((path, pathIndex) =>
          path.points.map((point, vertexIndex) => {
            const x = graphic.rect.x + point.x * graphic.rect.width;
            const y = graphic.rect.y + point.y * graphic.rect.height;
            const active = selected?.pathIndex === pathIndex && selected.vertexIndex === vertexIndex;
            return (
              <circle
                key={`${pathIndex}-${vertexIndex}`}
                data-testid="layout-vertex-handle"
                role="button"
                aria-label={`Vertex ${vertexIndex + 1}`}
                tabIndex={0}
                cx={x}
                cy={y}
                r={active ? 2.2 : 1.7}
                fill={active ? tokens.color.accent.primary : tokens.color.canvas.light}
                stroke={tokens.color.accent.primary}
                strokeWidth=".7"
                style={{ cursor: 'move' }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  const vertex = { pathIndex, vertexIndex };
                  setSelected(vertex);
                  setDragging(vertex);
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelected({ pathIndex, vertexIndex });
                    return;
                  }
                  const dx = event.key === 'ArrowLeft' ? -1
                    : event.key === 'ArrowRight' ? 1 : 0;
                  const dy = event.key === 'ArrowUp' ? -1
                    : event.key === 'ArrowDown' ? 1 : 0;
                  if (!dx && !dy) return;
                  event.preventDefault();
                  onGeometryChange(editLayoutGraphicVertex(
                    graphic.geometry,
                    pathIndex,
                    vertexIndex,
                    {
                      x: point.x + dx / Math.max(graphic.rect.width, 1),
                      y: point.y + dy / Math.max(graphic.rect.height, 1),
                    },
                  ));
                }}
              />
            );
          }),
        )}
      </svg>
    </div>
  );
}
